// ABOUTME: Draws the pre-built warehouse widget a dashboard cell asked for (the COMPONENT cell kind).
// ABOUTME: Sibling of RhDashboardChart / RhDashboardPivotBounded.
//
// A widget is an existing warehouse UI piece (an orders KPI card, a fulfillment chart, the employee table).
// Every embeddable one is listed in the catalogue (dashboardElements.js) under a short key. Two kinds:
//   - it fetches its own data (charts, day-totals widgets)  -> just draw it
//   - it is drawn from data we feed it (the KPI cards)       -> run its query, then draw it with the result
//
// About repeated cards: a fed widget names ONE query in the catalogue. Drop three outbound-orders cards on a
// dashboard and all three run that same query with the same "this month" arguments — RTK Query collapses
// identical query+args into a SINGLE request and shares the cached answer. Three cards = one fetch.

import { useMemo } from "react";

import { dashboardElements, dashboardElementSources } from "./dashboardElements";


// A widget that is drawn from fetched data: run its one query, then draw the widget with the result.
const DataBackedWidget = ({ widget, sx }) => {

    const dataQuery = dashboardElementSources[widget.source];

    // The arguments are the same all day, so the request is built once and (per the note above) shared.
    const queryArguments = useMemo(() => dataQuery.getArgs?.(), [dataQuery]);

    const { data, isLoading, isFetching } = dataQuery.useQuery(queryArguments, dataQuery.options);

    const Widget = widget.Component;

    // Orders cards read `totalData`, the others read `data` — pass both so we needn't special-case them.
    return (
        <Widget
            totalData={data}
            data={data}
            isLoading={isLoading}
            isFetching={isFetching}
            sx={sx}
        />
    );
};


export const RhDashboardComponent = ({ elementKey, sx }) => {

    const widget = dashboardElements[elementKey];

    // A widget that fetches its own data is drawn straight away; one with a `source` is fed by its query.
    const Widget = widget?.Component;

    // The registry can give an element default styling (e.g. `height:1` to stretch and fill the cell); the
    // caller's `sx` (a cell-level override) wins.
    const mergedSx = { ...widget?.sx, ...sx };

    return (
        <>
            {widget?.source
                ? <DataBackedWidget widget={widget} sx={mergedSx} />
                : <Widget sx={mergedSx} />}
        </>
    );
};
